import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Chip,
  Box,
  Typography
} from '@mui/material'
import { nomenclatorV2Api } from '../../services/nomenclatorV2Api'
import apiClient from '../../services/api'

interface SegmentEditDialogProps {
  open: boolean
  onClose: () => void
  segmentId: string
  onUpdate: () => void
}

export const SegmentEditDialog: React.FC<SegmentEditDialogProps> = ({
  open,
  onClose,
  segmentId,
  onUpdate
}) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [regions, setRegions] = useState<any[]>([])
  const [subregions, setSubregions] = useState<any[]>([])
  const [techniques, setTechniques] = useState<any[]>([])
  const [purposes, setPurposes] = useState<any[]>([])
  const [lateralities, setLateralities] = useState<any[]>([])

  const [form, setForm] = useState({
    detected_region: '',
    detected_subregion: '',
    detected_laterality: 'NONE',
    detected_techniques: [] as string[],
    detected_purpose: '',
    status: 'pending'
  })

  useEffect(() => {
    if (open) {
      loadSegment()
      loadVocabulary()
    }
  }, [open, segmentId])

  const loadSegment = async () => {
    try {
      setLoading(true)
      const response = await apiClient.get(`/segments/${segmentId}`)
      const segment = response.data
      setForm({
        detected_region: segment.detected_region || '',
        detected_subregion: segment.detected_subregion || '',
        detected_laterality: segment.detected_laterality || 'NONE',
        detected_techniques: segment.detected_techniques || [],
        detected_purpose: segment.detected_purpose || '',
        status: segment.status || 'pending'
      })
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Eroare la încărcarea segmentului')
    } finally {
      setLoading(false)
    }
  }

  const loadVocabulary = async () => {
    try {
      const overview = await nomenclatorV2Api.getOverview()
      setRegions(overview.regions || [])
      setSubregions(overview.subregions || [])
      setTechniques(overview.techniques || [])
      setPurposes(overview.purposes || [])
      setLateralities(overview.lateralities || [])
    } catch (err) {
      console.error('Failed to load vocabulary', err)
    }
  }

  const handleSave = async () => {
    try {
      setLoading(true)
      setError(null)
      await apiClient.patch(`/segments/${segmentId}`, {
        detected_region: form.detected_region,
        detected_subregion: form.detected_subregion || null,
        detected_laterality: form.detected_laterality,
        detected_techniques: form.detected_techniques,
        detected_purpose: form.detected_purpose || null,
        status: form.status
      })
      onUpdate()
      onClose()
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Eroare la salvarea modificărilor')
    } finally {
      setLoading(false)
    }
  }

  const filteredSubregions = subregions.filter((sub) => sub.parent_region_id === form.detected_region)

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Editează Segment</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Regiune</InputLabel>
              <Select
                value={form.detected_region}
                onChange={(e) => {
                  setForm((prev) => ({ ...prev, detected_region: e.target.value, detected_subregion: '' }))
                }}
                label="Regiune"
              >
                {regions.map((r) => (
                  <MenuItem key={r.id} value={r.id}>
                    {r.display_name_ro}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth disabled={!form.detected_region}>
              <InputLabel>Subregiune</InputLabel>
              <Select
                value={form.detected_subregion}
                onChange={(e) => setForm((prev) => ({ ...prev, detected_subregion: e.target.value }))}
                label="Subregiune"
              >
                <MenuItem value="">Niciuna</MenuItem>
                {filteredSubregions.map((sub) => (
                  <MenuItem key={sub.id} value={sub.id}>
                    {sub.display_name_ro}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Laterality</InputLabel>
              <Select
                value={form.detected_laterality}
                onChange={(e) => setForm((prev) => ({ ...prev, detected_laterality: e.target.value }))}
                label="Laterality"
              >
                {lateralities.map((lat) => (
                  <MenuItem key={lat.id} value={lat.id}>
                    {lat.display_name_ro}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Purpose</InputLabel>
              <Select
                value={form.detected_purpose}
                onChange={(e) => setForm((prev) => ({ ...prev, detected_purpose: e.target.value }))}
                label="Purpose"
              >
                <MenuItem value="">Niciunul</MenuItem>
                {purposes.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.display_name_ro}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="body2" gutterBottom>
              Techniques
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {techniques.map((tech) => {
                const isSelected = form.detected_techniques.includes(tech.id)
                return (
                  <Chip
                    key={tech.id}
                    label={tech.display_name_ro}
                    onClick={() => {
                      setForm((prev) => ({
                        ...prev,
                        detected_techniques: isSelected
                          ? prev.detected_techniques.filter((t) => t !== tech.id)
                          : [...prev.detected_techniques, tech.id]
                      }))
                    }}
                    color={isSelected ? 'primary' : 'default'}
                    variant={isSelected ? 'filled' : 'outlined'}
                  />
                )
              })}
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={form.status}
                onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value }))}
                label="Status"
              >
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="confirmed">Confirmed</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Anulează</Button>
        <Button onClick={handleSave} variant="contained" disabled={loading || !form.detected_region}>
          Salvează
        </Button>
      </DialogActions>
    </Dialog>
  )
}

